import { View, type ViewStyle } from "react-native";

import {
  AUDIO_WAVEFORM_BARS,
  AUDIO_WAVEFORM_BAR_RADIUS,
  AUDIO_WAVEFORM_GAP,
  AUDIO_WAVEFORM_HEIGHT,
  getAudioWaveformBarProgress,
} from "./recipe";
import type { AudioWaveformProps } from "./types";
import { LockBadge } from "../lock-badge/native";

export interface AudioWaveformNativeProps extends AudioWaveformProps {
  style?: ViewStyle;
}

export function AudioWaveform({
  activeColor,
  height = AUDIO_WAVEFORM_HEIGHT,
  inactiveColor,
  lockedColor = inactiveColor,
  lockPosition,
  onLockPress,
  progress = 0,
  style,
}: AudioWaveformNativeProps) {
  return (
    <View
      accessibilityElementsHidden={!onLockPress}
      importantForAccessibility={onLockPress ? "auto" : "no-hide-descendants"}
      style={[styles.waveform, { height, gap: AUDIO_WAVEFORM_GAP }, style]}
    >
      <View style={[styles.bars, { gap: AUDIO_WAVEFORM_GAP }]}>
        {AUDIO_WAVEFORM_BARS.map((barHeight, index) => {
          const locked =
            lockPosition !== undefined &&
            (index + 0.5) / AUDIO_WAVEFORM_BARS.length >= lockPosition;

          return (
            <View
              // The pattern is intentionally static, so its index is its identity.
              key={index}
              style={[
                styles.bar,
                {
                  height: height * barHeight,
                  backgroundColor: locked ? lockedColor : inactiveColor,
                },
              ]}
            >
              <View
                style={[
                  styles.progress,
                  {
                    width: `${getAudioWaveformBarProgress(progress, index) * 100}%`,
                    backgroundColor: activeColor,
                  },
                ]}
              />
            </View>
          );
        })}
      </View>
      {lockPosition !== undefined ? (
        <LockBadge
          accessibilityLabel="Log in to keep listening"
          onPress={onLockPress}
          size={20}
          style={{
            position: "absolute",
            zIndex: 2,
            top: "50%",
            left: `${Math.min(Math.max(lockPosition, 0), 1) * 100}%`,
            transform: [{ translateX: -10 }, { translateY: -10 }],
          }}
        />
      ) : null}
    </View>
  );
}

const styles = {
  waveform: {
    position: "relative",
    width: "100%",
    overflow: "visible",
  },
  bars: {
    width: "100%",
    height: "100%",
    flexDirection: "row",
    alignItems: "center",
    overflow: "hidden",
  },
  bar: {
    position: "relative",
    flex: 1,
    minWidth: 2.5,
    borderRadius: AUDIO_WAVEFORM_BAR_RADIUS,
    overflow: "hidden",
  },
  progress: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
  },
} satisfies Record<string, ViewStyle>;
